import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  shareReplay,
  take,
  timeout,
} from 'rxjs/operators';
import {
  currentAncestorOrigin,
  FRAME_AUTH_APP,
  isIframeWindow,
  isTrustedApiUrl,
  isTrustedFrameOrigin,
  readAccessCookie,
  resolveParentOrigin,
  TOKEN_WAIT_MS,
} from './frame-auth';
import { GameService } from './game.service';

export type FrameDeviceType = 'mobile' | 'desktop';

@Injectable({
  providedIn: 'root',
})
export class FrameAuthService implements OnDestroy {
  readonly access$ = new Subject<string>();
  readonly logout$ = new Subject<void>();
  readonly deviceType$ = new Subject<FrameDeviceType>();

  private readonly tokenFromParent$ = new Subject<string>();
  private readonly onMessage = (event: MessageEvent) =>
    this.handleMessage(event);

  private started = false;
  private receivedParentToken = false;
  private queryHost = '';
  private cachedParentOrigin: string | null | undefined;
  private refreshWait$: Observable<string> | null = null;

  constructor(private gameService: GameService) {}

  ngOnDestroy(): void {
    this.stop();
  }

  get isIframe(): boolean {
    return isIframeWindow();
  }

  get parentOrigin(): string | null {
    if (this.cachedParentOrigin === undefined) {
      this.cachedParentOrigin = resolveParentOrigin({
        referrer: document.referrer,
        ancestorOrigin: currentAncestorOrigin(),
        queryHost: this.queryHost || this.gameService.host,
      });
    }
    return this.cachedParentOrigin;
  }

  get hasHandshakeToken(): boolean {
    return this.receivedParentToken;
  }

  start(queryHost = ''): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.queryHost = queryHost;
    this.cachedParentOrigin = undefined;
    window.addEventListener('message', this.onMessage);
    if (this.isIframe) {
      this.sendHello();
    }
  }

  stop(): void {
    window.removeEventListener('message', this.onMessage);
    this.started = false;
  }

  readBootstrapCookie(): string | null {
    if (this.receivedParentToken) {
      return null;
    }
    return readAccessCookie();
  }

  sendHello(): void {
    this.postToParent({ type: 'auth:hello', app: FRAME_AUTH_APP });
  }

  sendExpired(): void {
    this.postToParent({ type: 'auth:expired', app: FRAME_AUTH_APP });
  }

  waitForFreshAccess(staleAccess: string): Observable<string> {
    if (!this.refreshWait$) {
      const stale = staleAccess;
      this.sendExpired();
      this.refreshWait$ = this.tokenFromParent$.pipe(
        filter((token) => !!token && token !== stale),
        take(1),
        timeout({ first: TOKEN_WAIT_MS }),
        catchError(() => of('')),
        finalize(() => {
          this.refreshWait$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.refreshWait$;
  }

  isTrustedApiRequest(url: string): boolean {
    return isTrustedApiUrl(url, this.gameService.host);
  }

  handleMessage(event: MessageEvent): void {
    if (!isTrustedFrameOrigin(event.origin)) {
      return;
    }

    const data = event.data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      if (data.type === 'auth:token') {
        this.applyParentAccess(data.access, {
          userId: data.userId,
          lang: data.lang,
        });
        return;
      }
      if (data.type === 'auth:logout') {
        this.applyLogout();
      }
      return;
    }

    if (typeof data !== 'string') {
      return;
    }

    const parts = data.split('::');
    if (parts[0] !== 'gamelib') {
      return;
    }
    if (parts[1] === 'token') {
      this.applyParentAccess(parts[2] || '');
      return;
    }
    if (parts[1] === 'deviceType') {
      const value = parts[2];
      if (value === 'mobile' || value === 'desktop') {
        this.deviceType$.next(value);
      }
    }
  }

  private applyParentAccess(
    access: string,
    meta?: { userId?: string; lang?: string }
  ): void {
    if (!access) {
      return;
    }
    this.receivedParentToken = true;
    this.gameService.token = access;
    if (meta?.userId && !this.gameService.userId) {
      this.gameService.userId = meta.userId;
    }
    if (meta?.lang && !this.gameService.hasLocale) {
      this.gameService.locale = meta.lang;
    }
    this.tokenFromParent$.next(access);
    this.access$.next(access);
  }

  private applyLogout(): void {
    this.gameService.token = '';
    this.gameService.myUser.next(null);
    this.logout$.next();
  }

  private postToParent(message: object): void {
    const origin = this.parentOrigin;
    if (!origin || !this.isIframe) {
      return;
    }
    window.parent.postMessage(message, origin);
  }
}
